import { supabase } from './supabase';

/** Columns every job list and the job workspace read from `bookings`. */
export const BOOKING_SELECT =
  'id, status, journey_stage, journey_stage_at, service_type, scheduled_at, created_at, confirmed_at, checked_in_at, in_progress_at, completed_at, cancelled_at, problem_description, cancel_reason, stay_option, pickup_delivery, pickup_notes, estimated_minutes, media, customer_id, vehicle_id, branch_id, preferred_mechanic_id, profiles:customer_id(first_name, last_name, full_name, phone, email), vehicles:vehicle_id(brand, model, year, plate_number, vin, mileage, previous_maintenance_notes, engine, transmission), branches:branch_id(name, address, phone), mechanic:preferred_mechanic_id(first_name, last_name, full_name), job_cards(id, reception_completed_at, assigned_technician_id, status)';

function jobCardsOf(row) {
  const cards = row?.job_cards;
  if (Array.isArray(cards)) return cards.filter(Boolean);
  if (cards && typeof cards === 'object') return [cards];
  return [];
}

export function bookingReceptionComplete(row) {
  if (row?.reception_completed === true) return true;
  return jobCardsOf(row).some((card) => !!card.reception_completed_at);
}

export function bookingAssignedTechnicianId(row) {
  const card = jobCardsOf(row).find((item) => !!item.assigned_technician_id);
  return card?.assigned_technician_id || null;
}

export function bookingHandedOver(row) {
  return jobCardsOf(row).some((card) => card.status === 'delivered');
}

/** Merge reception_completed flags from job_cards for a list of bookings. */
export async function hydrateReceptionFlags(rows) {
  if (!rows?.length) return rows || [];
  const ids = rows.map((row) => row.id).filter(Boolean);
  if (!ids.length) return rows;
  const { data, error } = await supabase
    .from('job_cards')
    .select('booking_id, reception_completed_at')
    .in('booking_id', ids)
    .not('reception_completed_at', 'is', null);
  if (error) return rows;
  const done = new Set((data || []).map((row) => row.booking_id));
  return rows.map((row) => ({
    ...row,
    reception_completed: done.has(row.id) || bookingReceptionComplete(row),
  }));
}

export async function updateBookingStatus(id, status) {
  const { data, error } = await supabase
    .from('bookings')
    .update({ status })
    .eq('id', id)
    .select(
      'id, status, journey_stage, journey_stage_at, confirmed_at, checked_in_at, in_progress_at, completed_at, cancelled_at, cancel_reason'
    )
    .maybeSingle();
  if (error) return { error };
  if (!data) {
    return {
      error: {
        message: 'Status was not saved. Your account cannot update this booking.',
      },
    };
  }
  return { data };
}

/**
 * The customer collected the car: issues the invoice from the approved quote
 * (paid or not yet paid), marks the job card delivered and thanks the
 * customer (hand_over_booking). Returns { invoice_id, invoice_status, total_amount }.
 */
export async function markBookingHandedOver(bookingId, paid = true) {
  const { data, error } = await supabase.rpc('hand_over_booking', {
    p_booking_id: bookingId,
    p_paid: paid,
  });
  if (error) return { error };
  return { data };
}

/** Mark the car as arrived without the digital check-in (optional check-in). */
export async function skipBookingReception(bookingId, reason) {
  const { data, error } = await supabase.rpc('skip_vehicle_reception', {
    p_booking_id: bookingId,
    p_reason: reason?.trim() || null,
  });
  if (error) return { error };
  return { data };
}

/**
 * Latest customer-facing quote status per booking: 'sent' (waiting on the
 * customer), 'approved' or 'rejected'.
 */
export async function fetchQuoteStatuses(bookingIds) {
  let query = supabase
    .from('quotations')
    .select('booking_id, status, created_at')
    .in('status', ['sent', 'approved', 'rejected'])
    .order('created_at', { ascending: true });
  if (bookingIds?.length) query = query.in('booking_id', bookingIds);
  const { data, error } = await query;
  if (error) throw error;
  const map = new Map();
  for (const row of data || []) {
    if (row.booking_id) map.set(row.booking_id, row.status);
  }
  return map;
}

/** Technicians keyed by id, for showing who is on each job. */
export async function fetchTechnicians() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, full_name, branch_id')
    .eq('role', 'technician');
  if (error) throw error;
  return data || [];
}

/**
 * Pending mechanic requests (parts, quality check) per booking:
 * Map<bookingId, { parts: number, qc: number }>.
 */
export async function fetchPendingMechanicRequests() {
  const [parts, qc] = await Promise.all([
    supabase
      .from('technician_parts_requests')
      .select('id, job_cards(booking_id)')
      .eq('status', 'pending'),
    supabase
      .from('technician_qc_requests')
      .select('id, job_cards(booking_id)')
      .eq('status', 'pending'),
  ]);
  const map = new Map();
  const add = (rows, key) => {
    for (const row of rows || []) {
      const bookingId = row.job_cards?.booking_id;
      if (!bookingId) continue;
      const entry = map.get(bookingId) || { parts: 0, qc: 0 };
      entry[key] += 1;
      map.set(bookingId, entry);
    }
  };
  if (!parts.error) add(parts.data, 'parts');
  if (!qc.error) add(qc.data, 'qc');
  return map;
}

export async function fetchPartsRequests(jobCardId) {
  const { data, error } = await supabase
    .from('technician_parts_requests')
    .select('*')
    .eq('job_card_id', jobCardId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/** approved = ordered, fulfilled = arrived, rejected = not needed. */
export async function setPartsRequestStatus(id, status, userId) {
  const patch = { status };
  if (status === 'fulfilled' || status === 'rejected') {
    patch.resolved_at = new Date().toISOString();
    patch.resolved_by = userId || null;
  }
  const { data, error } = await supabase
    .from('technician_parts_requests')
    .update(patch)
    .eq('id', id)
    .select('*')
    .maybeSingle();
  if (error) return { error };
  if (!data) return { error: { message: 'Request was not saved. Your account cannot update it.' } };
  return { data };
}

/**
 * Subscribe to booking changes (the table is in the realtime publication).
 * Returns an unsubscribe function. `bookingId` narrows it to one booking;
 * `onStatus(connected)` reports whether the live connection is up.
 */
export function subscribeToBookings(onChange, bookingId, onStatus) {
  const name = bookingId ? `desk-booking-${bookingId}` : `desk-bookings-${Math.random().toString(36).slice(2)}`;
  const channel = supabase
    .channel(name)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'bookings',
        ...(bookingId ? { filter: `id=eq.${bookingId}` } : {}),
      },
      () => onChange(),
    )
    .subscribe((status) => onStatus?.(status === 'SUBSCRIBED'));
  return () => {
    supabase.removeChannel(channel);
  };
}
